import { isVisibleComponetBaseOnRole } from "@/commons/RoleFunction.ts";
import { Calendar, Input, EAlignment, EPosition } from "@/dependencies";
import {
  Field,
  FieldProps,
  makeStyles,
  Popover,
  PopoverSurface,
  PositioningImperativeRef,
  Slot,
  useRestoreFocusTarget
} from "@fluentui/react-components";
import { CalendarLtrRegular } from "@fluentui/react-icons";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { DatePickerLabelWrapper, DatePickerWrapper } from "./Style";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { getCalendarStringsFromLocale, getDateFnsLocaleFromI18n } from "@/utils/dateFnsLocalizer";
import { DayOfWeek } from "@fluentui/react-calendar-compat";

const useStyles = makeStyles({
  root: {
    fontWeight: 600
  },
  disabled: {
    backgroundColor: "var(--colorSubtleBackgroundHover)",
    "& input": {
      color: "var(--colorNeutralForeground1)"
    }
  }
});

interface IDatePickerProps {
  rcName?: string;
  theme?: string;
  onSelectDate?: (date: Date) => void;
  selectedDate?: Date;
  rcRole?: string[];
  requireRoles?: string[];
  requireOwner?: boolean;
  isOwner?: boolean;
  disabled?: boolean;
  placeholder?: string;
  format?: string;
  minDate?: Date;
  maxDate?: Date;
  contentBefore?: Slot<"span">;
  firstDayOfWeek?: DayOfWeek;
  appearance?:
    | "outline"
    | "underline"
    | "filled-darker"
    | "filled-lighter"
    | "filled-darker-shadow"
    | "filled-lighter-shadow";
  displayLabel?: React.ReactNode;
}

const DatePickerCustom = (props: IDatePickerProps & FieldProps) => {
  const styles = useStyles();
  const isCanBeVisible =
    isVisibleComponetBaseOnRole(props.rcRole, props.requireRoles) &&
    (props.requireOwner ? props.isOwner : true);

  const wrapperRef = useRef<HTMLInputElement | null>(null);
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const headerId = useId();

  useEffect(() => {
    if (wrapperRef?.current && positioningRef.current) {
      positioningRef.current?.setTarget(wrapperRef.current);
    }
  }, [wrapperRef, positioningRef]);

  useEffect(() => {
    if (props.selectedDate) {
      setSelectedDate(props.selectedDate);
    }
  }, [props.selectedDate]);

  const onSelectDate = (date: Date) => {
    setSelectedDate(date);
    if (props.onSelectDate) {
      props.onSelectDate(date);
    }
    setOpen(false);
  };

  const getWithElement = (): number => {
    const el = document.getElementById(`select_context_${props.rcName || "input"}`);
    return el?.offsetWidth ? el.offsetWidth : 0;
  };

  const isDisabled = props.disabled || !isCanBeVisible;

  const { t, i18n } = useTranslation();
  const dateFnsLocale = useMemo(() => getDateFnsLocaleFromI18n(i18n.language), [i18n.language]);

  const calendarStrings = useMemo(() => {
    const { months, shortMonths, days, shortDays } = getCalendarStringsFromLocale(dateFnsLocale);
    const strings = {
      months,
      shortMonths,
      days,
      shortDays,
      goToToday: t("button.go-to-today.title", "Go to today"),
      prevMonthAriaLabel: t("button.previous.title", "Previous"),
      nextMonthAriaLabel: t("button.next.title", "Next"),
      prevYearAriaLabel: t("button.previous.title", "Previous"),
      nextYearAriaLabel: t("button.next.title", "Next")
    };

    return strings;
  }, [dateFnsLocale, t]);

  return (
    <DatePickerWrapper
      className='DatePickerWrapper'
      ref={wrapperRef}
      id={`select_context_${props.rcName || "input"}`}
    >
      {props.displayLabel ? (
        <DatePickerLabelWrapper onClick={isCanBeVisible ? () => setOpen((s) => !s) : undefined}>
          {props.displayLabel}
        </DatePickerLabelWrapper>
      ) : (
        <Field
          label={props.label}
          validationMessage={props.validationMessage}
          validationMessageIcon={props.validationMessageIcon}
          validationState={props.validationState}
          required={props.required}
          style={props.style}
          className={`${props.className} ${styles.root}`}
        >
          <Input
            {...restoreFocusTargetAttribute}
            onClick={isCanBeVisible ? () => setOpen((s) => !s) : undefined}
            style={{ width: "100%", cursor: "pointer" }}
            disabled={isDisabled}
            value={selectedDate ? format(selectedDate, props.format || "MM/dd/yyyy") : ""}
            contentAfter={
              isDisabled ? null : (
                <CalendarLtrRegular
                  onClick={isCanBeVisible ? () => setOpen((s) => !s) : undefined}
                />
              )
            }
            contentBefore={props.contentBefore}
            className={`${isDisabled ? styles.disabled : ""}`}
            appearance={props.appearance}
          />
        </Field>
      )}
      <Popover
        onOpenChange={(_, data) => setOpen(data.open)}
        trapFocus
        open={open}
        positioning={{
          positioningRef,
          position: EPosition.BELOW,
          align: EAlignment.START
        }}
        mountNode={undefined}
      >
        <PopoverSurface
          aria-labelledby={headerId}
          className='width_custom_popover'
          style={{
            maxHeight: "300px",
            overflow: "auto",
            width: getWithElement() - 10,
            minWidth: "200px",
            padding: "4px"
          }}
        >
          <Calendar
            key={`datepicker-event-${i18n.language}`}
            showGoToToday
            showMonthPickerAsOverlay
            highlightSelectedMonth
            selectedDate={selectedDate || undefined}
            onChange={onSelectDate}
            isMonthPickerVisible={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            strings={calendarStrings}
            firstDayOfWeek={props.firstDayOfWeek}
            width={getWithElement() - 10}
          />
        </PopoverSurface>
      </Popover>
    </DatePickerWrapper>
  );
};

export default DatePickerCustom;
